import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
} from '@microsoft/applicationinsights-react-js';
import { initAppInsights, type AppTelemetry } from './appInsights';

export interface TelemetryProviderProps {
  connectionString: string | undefined;
  appName: string;
  errorFallback?: React.ReactNode;
  children: React.ReactNode;
}

const RouteTracker: React.FC<{ telemetry: AppTelemetry }> = ({ telemetry }) => {
  const location = useLocation();
  const previous = useRef<string>('');

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    if (path === previous.current) return;
    previous.current = path;
    telemetry.reactPlugin.trackPageView({ uri: path, name: path });
  }, [location.pathname, location.search, telemetry]);

  return null;
};

export const TelemetryProvider: React.FC<TelemetryProviderProps> = ({
  connectionString,
  appName,
  errorFallback = null,
  children,
}) => {
  const telemetry = useMemo(
    () => initAppInsights(connectionString),
    [connectionString]
  );

  useEffect(() => {
    if (!telemetry) return;
    telemetry.appInsights.addTelemetryInitializer((item) => {
      item.tags = item.tags ?? {};
      item.tags['ai.cloud.role'] = `fms-${appName}`;
    });
  }, [telemetry, appName]);

  if (!telemetry) return <>{children}</>;

  return (
    <AppInsightsContext.Provider value={telemetry.reactPlugin}>
      <AppInsightsErrorBoundary
        appInsights={telemetry.reactPlugin}
        onError={() => <>{errorFallback}</>}
      >
        <RouteTracker telemetry={telemetry} />
        {children}
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  );
};

export default TelemetryProvider;
